import React from 'react';
import ReactStately from 'react-stately';

import { t } from '@ping/helpers';
import { FormStructureContext } from '@ping/pages/trade/components/MarketOrder/MarketOrderForm/OrderForm/OrderFormStructure/FormStructure/form-structure.context';
import { SelectA11Y } from '@ping/uikit';

interface Token {
  id: string;
  label?: string;
}

interface IOrderTokenSelectProps {
  className?: string;
  tokens: Token[];
  isMultiple?: true;
  isDisabled?: true;
  label: string;
  name: string;
  onChange?(amount: 1, key: string): void;
  onChange?(amount: number, keys: string[]): void;
}

export function PortfolioTokenSelect(props: IOrderTokenSelectProps) {
  const formContext = React.useContext(FormStructureContext);
  const [selectedTokenKeys, setSelectedTokenKeys] = React.useState<ReactStately.Selection>(new Set());

  const getButtonContent = () => {
    if (selectedTokenKeys === 'all') {
      return `${props.tokens
        .slice(0, 4)
        .map(tk => tk.label ?? tk.id)
        .join(', ')} ... (${props.tokens.length})`;
    }

    const ids = Array.from(selectedTokenKeys as unknown as Set<string>);
    if (!ids.length) {
      return t('Select token');
    }

    const labels = ids.map(k => props.tokens.find(tk => tk.id === k)?.label || String(k));
    const shown = labels.slice(0, 4);
    const extra = labels.length - shown.length;
    return extra > 0 ? `${shown.join(', ')} ... (${labels.length})` : shown.join(', ');
  };

  const handleMultiSelectionChange = (keys: ReactStately.Selection) => {
    const updated = keys !== 'all' ? keys : new Set(props.tokens.map(t => t.id));
    setSelectedTokenKeys(updated);
    const selections = [...updated.values()] as string[];
    props.onChange(updated.size, selections);
  };

  const handleSingleSelectionChange = (selection: ReactStately.Key) => {
    setSelectedTokenKeys(new Set<ReactStately.Key>().add(selection));
    props.onChange(1, selection as string);
  };

  React.useEffect(() => {
    if (formContext.orderState?.isSuccess) {
      setSelectedTokenKeys(new Set());
    }
  }, [formContext.orderState?.status]);

  return (
    <SelectA11Y
      className={props.className}
      label={props.label}
      name={props.name}
      items={props.tokens}
      isDisabled={props.isDisabled}
      onSelectionChange={handleSingleSelectionChange}
      isMultiple={props.isMultiple}
      multiSelectedKeys={selectedTokenKeys}
      onMultiSelectionChange={handleMultiSelectionChange}
      buttonContent={getButtonContent()}
      emptyState={t("You don't have any available tokens for this asset")}
    >
      {token => (
        <SelectA11Y.Item key={token.id} aria-label={token.id}>
          {token.label ?? token.id}
        </SelectA11Y.Item>
      )}
    </SelectA11Y>
  );
}
